import type {
  DeckIR,
  ExternalDependency,
  FidelityReport,
  FidelityStrategy,
  FontSubstitution,
  ParseDiagnostic,
  Shape,
  SlideFidelity,
  SlideIR,
} from '@slideforge/shared';
import { walkShapes } from '@slideforge/shared';

/**
 * M2 stage E — the fidelity strategy selector.
 *
 * Honesty here is a feature: the author is told before publishing what will not
 * survive. Every non-Vector strategy records why, and `reasons` is never empty
 * (docs/plans/41 §4 treats an empty reason list as a defect).
 */

export interface FidelityInput {
  readonly deck: DeckIR;
  readonly diagnostics: readonly ParseDiagnostic[];
  /** Constructs the render compiler could not represent, keyed by slide index. */
  readonly droppedBySlideIndex?: ReadonlyMap<number, readonly string[]>;
  readonly courseVersionHash?: string;
  readonly generatedAt?: string;
  /** Fonts the delivery target can actually serve. Anything else is substituted. */
  readonly availableFonts?: ReadonlySet<string>;
}

const HYBRID_FRAMES: Readonly<Record<string, string>> = {
  chart: 'A chart is drawn from its exported image, so it will not animate or resize like a live chart.',
  smartArt: 'SmartArt is drawn from its fallback picture rather than rebuilt as shapes.',
  ole: 'An embedded object (OLE) is shown as a static picture.',
  model3d: 'A 3D model is shown as a static picture from its current camera angle.',
  unknown: 'An unrecognised embedded object is shown as a static picture.',
};

export function buildFidelityReport(input: FidelityInput): FidelityReport {
  const { deck, diagnostics } = input;
  const slides = deck.slides.map((slide) =>
    analyseSlide(slide, input.droppedBySlideIndex?.get(slide.index) ?? [], diagnostics),
  );

  return {
    generatedAt: input.generatedAt ?? new Date().toISOString(),
    courseVersionHash: input.courseVersionHash ?? '',
    slides,
    fonts: collectFontSubstitutions(deck, input.availableFonts),
    externalDependencies: collectExternalDependencies(deck),
    diagnostics,
    blocking: diagnostics.filter((d) => d.severity === 'blocking'),
  };
}

function analyseSlide(
  slide: SlideIR,
  dropped: readonly string[],
  diagnostics: readonly ParseDiagnostic[],
): SlideFidelity {
  const reasons: string[] = [];
  const warnings: string[] = [];
  const droppedConstructs = new Set(dropped);
  let strategy: FidelityStrategy = 'Vector';

  for (const shape of walkShapes(slide.shapes)) {
    if (shape.kind === 'graphicFrame') {
      strategy = 'Hybrid';
      reasons.push(HYBRID_FRAMES[shape.frameKind] ?? HYBRID_FRAMES['unknown'] ?? 'Embedded object.');
      droppedConstructs.add(`graphicFrame:${shape.frameKind}`);
      if (!shape.fallbackMediaId) {
        warnings.push(
          `"${shape.name}" has no fallback picture stored in the file, so nothing can be drawn for it.`,
        );
      }
      continue;
    }

    if (shape.kind === 'shape' && hasWordArtEffects(shape)) {
      strategy = strategy === 'Vector' ? 'Hybrid' : strategy;
      reasons.push(`"${shape.name}" uses text effects that are approximated in the browser.`);
      droppedConstructs.add('text:wordArt');
    }

    if (shape.kind === 'media' && !shape.mediaId && shape.linkedTarget) {
      warnings.push(`"${shape.name}" points at a file outside the deck and will not play.`);
    }
  }

  // Anything the compiler had to skip that is not already explained.
  for (const construct of dropped) {
    if (construct.startsWith('geometry:')) {
      strategy = strategy === 'Vector' ? 'Hybrid' : strategy;
      reasons.push(`The shape "${construct.slice('geometry:'.length)}" is drawn as a rectangle.`);
    }
  }

  const slideDiagnostics = diagnostics.filter((d) => d.slideIndex === slide.index);
  for (const d of slideDiagnostics) {
    if (d.severity === 'error') warnings.push(d.message);
  }

  // A slide whose content is almost entirely unrepresentable is better served by
  // a flat image than by a pile of wrong rectangles.
  if (strategy === 'Hybrid' && reasons.length >= 6) {
    strategy = 'Raster';
    reasons.unshift('Most of this slide cannot be rebuilt in the browser, so it is published as an image.');
  }

  return {
    slideIndex: slide.index,
    strategy,
    reasons: dedupe(reasons),
    droppedConstructs: [...droppedConstructs].sort(),
    warnings: dedupe(warnings),
  };
}

function hasWordArtEffects(shape: Extract<Shape, { kind: 'shape' }>): boolean {
  if (!shape.text) return false;
  return shape.text.paragraphs.some((p) =>
    p.runs.some((r) => r.props.outline !== undefined || r.props.highlight !== undefined),
  );
}

/**
 * Fonts referenced by the deck that the delivery target cannot serve. With no
 * font list supplied nothing is reported, because guessing at substitutions
 * would fill the report with noise the author cannot act on.
 */
export function collectFontSubstitutions(
  deck: DeckIR,
  available: ReadonlySet<string> | undefined,
): FontSubstitution[] {
  if (!available) return [];
  const used = new Map<string, Set<number>>();

  const note = (font: string | undefined, slideIndex: number) => {
    if (!font || font === '' || font.startsWith('+')) return;
    const set = used.get(font) ?? new Set<number>();
    set.add(slideIndex);
    used.set(font, set);
  };

  for (const slide of deck.slides) {
    for (const shape of walkShapes(slide.shapes)) {
      if (shape.kind !== 'shape' || !shape.text) continue;
      for (const p of shape.text.paragraphs) {
        for (const r of p.runs) {
          note(r.props.latinFont, slide.index);
          note(r.props.eastAsianFont, slide.index);
          note(r.props.complexScriptFont, slide.index);
        }
      }
    }
  }

  const out: FontSubstitution[] = [];
  for (const [font, slides] of used) {
    if (available.has(font)) continue;
    out.push({
      requested: font,
      substitutedWith: 'system-ui',
      reason: 'The font is not embedded in the deck and is not available to the player.',
      affectedSlides: [...slides].sort((a, b) => a - b),
    });
  }
  return out.sort((a, b) => a.requested.localeCompare(b.requested));
}

const YOUTUBE = /(?:youtube\.com|youtu\.be)/i;
const VIMEO = /vimeo\.com/i;

export function collectExternalDependencies(deck: DeckIR): ExternalDependency[] {
  const out: ExternalDependency[] = [];
  for (const slide of deck.slides) {
    for (const shape of walkShapes(slide.shapes)) {
      const url = shape.hyperlink?.kind === 'external' ? shape.hyperlink.url : undefined;
      if (!url) continue;
      out.push({
        kind: YOUTUBE.test(url) ? 'youtube' : VIMEO.test(url) ? 'vimeo' : 'web',
        url,
        slideIndex: slide.index,
        // A hyperlink degrades to plain text if unreachable, which is a fallback.
        hasStaticFallback: true,
      });
    }
  }
  return out;
}

function dedupe(items: readonly string[]): string[] {
  return [...new Set(items)];
}
